import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkle,
  CircleNotch,
  DiceFive,
  Eraser,
  Lightning,
  ChatsTeardrop,
  UploadSimple,
  Link as LinkIcon,
  Info,
  ArrowRight,
  CaretDown,
  Check,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { toast } from "sonner";
import FileUpload from "./FileUpload";
import { pickActiveIndex } from "../lib/activeAlap";

/** Stagger animation for revealed sections */
const containerStagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.04 },
  },
};
const itemFade = {
  hidden: { opacity: 0, y: -10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 0.9, 0.32, 1] } },
};

// Neutral, subtle field surface - lets the purple-tinted page background do the work
const PURPLE_FIELD = {
  background: "rgba(255,255,255,0.025)",
  border: "1px solid rgba(255,255,255,0.06)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02)",
};

export default function AgentInputScreen({
  accent,
  agentKey,
  eyebrow,
  title,
  subtitle,
  greeting,
  modePrompt = "Hogyan szeretnéd felépíteni?",
  guidedIntro,
  fastLabel = "Brand Express",
  fastDesc = "Elemezd az anyagaimat",
  guidedLabel = "Brand Építő",
  guidedDesc = "Kérdezz-Felelek",
  steps,
  values,
  setValues,
  onGenerate,
  generating,
  pickRandomSample,
  randomNiche,
  onRandomTest,
  onClear,
  clearing,
  nichePills,
  onPickNiche,
  fastTrackKeys,
  cycleDemoPill = false,
  contentInputMode = "classic", // 'classic' | 'split'
  eyebrowVariant = "brand", // 'brand' | 'avatar'
  showContentInput = true,
  fastVariant = "upload", // 'upload' | 'voice-source' | 'triad'
  requiresAvatarSource = false,
  genProgress = "",
  genError = null,
  onClearGenError,
  onContentChange,
  initialMode = null,
  initialWritingSample = "",
  initialPaste = "",
  initialAddedUrls = [],
}) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [writingSample, setWritingSample] = useState(initialWritingSample);
  const [url, setUrl] = useState("");
  const [urlBusy, setUrlBusy] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const [urlError, setUrlError] = useState("");
  const fileUploadRef = useRef(null);
  const writingSampleFileRef = useRef(null);
  const [writingSampleBusy, setWritingSampleBusy] = useState(false);
  const [kbFileCount, setKbFileCount] = useState(0);
  const [urlAddedCount, setUrlAddedCount] = useState(0);
  const [usedExamplePills, setUsedExamplePills] = useState({});
  const [demoCounter, setDemoCounter] = useState(0);
  const [demoLoading, setDemoLoading] = useState(false);
  // Brand Építő "content input" section - independent state (per-session).
  // These values flow into the generated user_context via the parent.
  const [pasteContent, setPasteContent] = useState(initialPaste);
  const [addedUrls, setAddedUrls] = useState(
    Array.isArray(initialAddedUrls) ? initialAddedUrls : []
  );

  // Avatar Express ("voice-source" or "voice-source-triad") — list of
  // all saved Voice blueprints, most recent first.
  const [voiceSources, setVoiceSources] = useState(null);
  const [selectedVoiceIdx, setSelectedVoiceIdx] = useState(0);
  const isVoiceSourceVariant = fastVariant === "voice-source";
  useEffect(() => {
    if (!isVoiceSourceVariant || mode !== "fast") return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/asset-vault");
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        const voices = items.filter((it) => it.agent_type === "voice");
        if (alive) {
          setVoiceSources(voices);
          // Default the source row to the active Márkahang chosen in
          // SidebarAktivAlap (if set), so downstream generation uses it
          // automatically. Falls back to most-recent (idx 0) otherwise.
          setSelectedVoiceIdx(pickActiveIndex("voice", voices));
        }
      } catch {
        if (alive) setVoiceSources([]);
      }
    })();
    return () => { alive = false; };
  }, [isVoiceSourceVariant, mode]);
  const selectedVoice = Array.isArray(voiceSources) && voiceSources.length > 0
    ? voiceSources[Math.min(selectedVoiceIdx, voiceSources.length - 1)]
    : null;

  // Ajánlat Varázsló page-level Avatar source row (shown ABOVE the mode
  // selector when requiresAvatarSource=true, on BOTH modes). Loads all
  // saved Avatar blueprints; default selection = most recent.
  const [avatarSources, setAvatarSources] = useState(
    requiresAvatarSource ? null : []
  );
  const [selectedAvatarIdx, setSelectedAvatarIdx] = useState(0);
  useEffect(() => {
    if (!requiresAvatarSource) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/asset-vault");
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        const avs = items.filter((it) => it.agent_type === "client");
        if (alive) {
          setAvatarSources(avs);
          // Default to the active Avatar chosen in SidebarAktivAlap (if set).
          // Falls back to most-recent (idx 0) when no active selection.
          setSelectedAvatarIdx(pickActiveIndex("client", avs));
        }
      } catch {
        if (alive) setAvatarSources([]);
      }
    })();
    return () => { alive = false; };
  }, [requiresAvatarSource]);
  const selectedAvatar =
    requiresAvatarSource && Array.isArray(avatarSources) && avatarSources.length > 0
      ? avatarSources[Math.min(selectedAvatarIdx, avatarSources.length - 1)]
      : null;

  const sampleLen = writingSample.trim().length;
  const hasKbContent = kbFileCount > 0 || urlAddedCount > 0 || sampleLen >= 50;

  // Offer Express pill: at least one of paste/URLs/files must be filled. The
  // triad's paste textarea writes to `writingSample` state (NOT pasteContent
  // — that one is used by Brand Építő's split content section). No 50-char
  // minimum since sales-letter snippets can be short.
  const hasTriadContent =
    sampleLen > 0 ||
    (Array.isArray(addedUrls) && addedUrls.length > 0) ||
    kbFileCount > 0;

  // Per-step validation - supports all step types.
  const isStepValid = (s) => {
    const v = values[s.key];
    switch (s.type) {
      case "pills": {
        const sel = Array.isArray(v?.selected) ? v.selected : [];
        const custom = (v?.custom || "").trim();
        const total = sel.length + (custom ? 1 : 0);
        return total >= (s.minSelect ?? 2) && total <= (s.maxSelect ?? 4);
      }
      case "sliders":
        // Always valid - defaults present
        return true;
      case "tags":
        // Optional by default
        return s.required ? Array.isArray(v) && v.length > 0 : true;
      case "textarea":
      default:
        return (v || "").toString().trim().length > 0;
    }
  };

  const allFilled = steps.every(isStepValid);
  const hasVoiceSource = isVoiceSourceVariant && !!selectedVoice;
  const avatarOK = !requiresAvatarSource || !!selectedAvatar;
  const canGenerate =
    !generating &&
    avatarOK &&
    (mode === "guided"
      ? allFilled
      : fastVariant === "voice-source"
        ? hasVoiceSource
        : fastVariant === "voice-source-triad"
          ? hasTriadContent
          : fastVariant === "triad"
            ? hasTriadContent
            : hasKbContent);

  const handleAddUrl = async () => {
    const u = url.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) {
      toast.error("Az URL http:// vagy https:// -vel kezdődjön");
      return;
    }
    setUrlBusy(true);
    try {
      await api.post("/rag/url", { url: u, agent_type: "foundation" });
      toast.success("URL feldolgozva - bekerült az Alapozás tudásbázisba");
      setUrl("");
      setUrlAddedCount((c) => c + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "URL beolvasása sikertelen");
    } finally {
      setUrlBusy(false);
    }
  };

  const handleGenerate = () => {
    if (mode === "fast") {
      if (fastVariant === "voice-source") {
        if (!hasVoiceSource) {
          toast.error("Először mentsd el a Márkahangod a Voice ügynökben.");
          return;
        }
        // Backend uses the selected saved Voice (voice_source_id) as the
        // brand_voice_context. The 6 input fields are required by Pydantic
        // validation, so we send a synthesizing seed instructing the AI to
        // derive the avatar from the chosen Voice.
        const seed =
          "Származtasd az ideális ügyfélprofilt a már mentett Márkahangom alapján - elemezd ki, kinek szól ez a hang, mi a fájdalmuk, mit akarnak elérni, ki ez a személy, és pontosan hogyan segítek nekik.";
        const synthetic = { voice_source_id: selectedVoice.id };
        const keys = Array.isArray(fastTrackKeys) && fastTrackKeys.length > 0
          ? fastTrackKeys
          : steps.map((s) => s.key);
        keys.forEach((k) => { synthetic[k] = seed; });
        onGenerate(synthetic);
        return;
      }
      if (fastVariant === "voice-source-triad") {
        // Avatar Express: Triád (paste/files/URL) as the sole client material.
        // No Márkahang dependency.
        if (!hasTriadContent) {
          toast.error("Tölts fel egy ügyfél-anyagot a generáláshoz.");
          return;
        }
        onGenerate();
        return;
      }
      if (fastVariant === "triad") {
        // Offer Express: call onGenerate() with NO synthetic arg so the
        // parent's serializeValues() can build the payload from BOTH the
        // Triád content (content.paste / content.urls) AND the Avatar
        // source id (content.avatarSourceId). Gates on hasTriadContent.
        if (!hasTriadContent) {
          toast.error("Tölts fel egy fájlt, illessz be egy ajánlat-tartalmat, vagy adj meg URL-t.");
          return;
        }
        onGenerate();
        return;
      }
      if (!hasKbContent) {
        toast.error("Tölts fel anyagot, illessz be írásmintát, vagy adj meg URL-t");
        return;
      }
      const seed = sampleLen >= 50
        ? writingSample.trim()
        : "Elemezd a Tudásbázisba feltöltött anyagokat (PDF / szövegek / URL-ek), és ezek alapján szintetizáld a márkahangot, az írásstílust, a tone-ot és a tipikus fordulatokat.";
      const synthetic = {};
      const keys = Array.isArray(fastTrackKeys) && fastTrackKeys.length > 0
        ? fastTrackKeys
        : steps.map((s) => s.key);
      keys.forEach((k) => { synthetic[k] = seed; });
      onGenerate(synthetic);
      return;
    }
    onGenerate();
  };

  // Handlers for typed-step interactions
  const setTextValue = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));

  const togglePill = (step, pillLabel) => {
    const cur = values[step.key] || { selected: [], custom: "" };
    const selected = Array.isArray(cur.selected) ? [...cur.selected] : [];
    const custom = cur.custom || "";
    const isSelected = selected.includes(pillLabel);
    const customCount = custom.trim() ? 1 : 0;
    const total = selected.length + customCount;
    if (isSelected) {
      const next = selected.filter((p) => p !== pillLabel);
      setValues((prev) => ({ ...prev, [step.key]: { selected: next, custom } }));
    } else {
      if (total >= (step.maxSelect ?? 4)) {
        toast.message(`Max ${step.maxSelect ?? 4} választható.`);
        return;
      }
      setValues((prev) => ({
        ...prev,
        [step.key]: { selected: [...selected, pillLabel], custom },
      }));
    }
  };

  const setPillCustom = (step, val) => {
    const cur = values[step.key] || { selected: [], custom: "" };
    setValues((prev) => ({
      ...prev,
      [step.key]: { selected: cur.selected || [], custom: val },
    }));
  };

  const setSliderValue = (step, idx, val) => {
    const cur = Array.isArray(values[step.key]) ? [...values[step.key]] : [];
    const defaults = (step.sliders || []).map(() => 50);
    const base = cur.length === (step.sliders || []).length ? cur : defaults;
    base[idx] = val;
    setValues((prev) => ({ ...prev, [step.key]: base }));
  };

  const addTag = (step, tagText) => {
    const t = (tagText || "").trim();
    if (!t) return false;
    const cur = Array.isArray(values[step.key]) ? values[step.key] : [];
    if (cur.includes(t)) return false;
    setValues((prev) => ({ ...prev, [step.key]: [...cur, t] }));
    return true;
  };

  const removeTag = (step, tag) => {
    const cur = Array.isArray(values[step.key]) ? values[step.key] : [];
    setValues((prev) => ({ ...prev, [step.key]: cur.filter((t) => t !== tag) }));
  };

  const markExamplePillUsed = (stepKey, idx) => {
    setUsedExamplePills((prev) => {
      const existing = prev[stepKey] || new Set();
      const next = new Set(existing);
      next.add(idx);
      return { ...prev, [stepKey]: next };
    });
  };

  const handleNichePick = (niche) => {
    setUsedExamplePills({});
    if (typeof onPickNiche === "function") onPickNiche(niche);
  };

  const handleCycleDemo = () => {
    if (!Array.isArray(nichePills) || nichePills.length === 0) return;
    const niche = nichePills[demoCounter % nichePills.length];
    setUsedExamplePills({});
    if (typeof onPickNiche === "function") onPickNiche(niche);
    setDemoCounter((c) => (c + 1) % nichePills.length);
  };

  // Márkahang / Ajánlat Írásminta ingesztálás — Fájl és URL végpontokra
  // közvetlenül a `writingSample` (BrandVoice triad) vagy `pasteContent`
  // (Content Ingest) mezőbe fűz. Ha van már ott szöveg, elválasztóval
  // egészíti ki, sosem írja felül némán.
  const appendWritingSample = (extra) => {
    if (!extra || typeof extra !== "string") return;
    // A látható ingest textarea majdnem mindenütt a `writingSample` — csak a
    // Content Ingest blokk (Ajánlat pastes vagy KB content) használ külön
    // `pasteContent` mezőt. A UI variant alapján döntünk: ha a látható
    // textarea a `pasteContent` (content-ingest), akkor arra fűzünk; egyébként
    // mindig `writingSample`-re.
    const targetPaste = fastVariant === "content-ingest";
    if (targetPaste) {
      setPasteContent((prev) => {
        if (!prev || !prev.trim()) return extra;
        return `${prev.trim()}\n\n--- ÚJ ÍRÁSMINTA ---\n\n${extra}`;
      });
    } else {
      setWritingSample((prev) => {
        if (!prev || !prev.trim()) return extra;
        return `${prev.trim()}\n\n--- ÚJ ÍRÁSMINTA ---\n\n${extra}`;
      });
    }
  };

  const handleWritingSampleFile = async (files) => {
    if (!files || !files.length) return;
    setWritingSampleBusy(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        const res = await api.post("/writing-sample/extract-file", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const text = res?.data?.text || "";
        if (text) {
          appendWritingSample(text);
          toast.success(`Írásminta betöltve: ${f.name} (${res.data.chars} karakter)`);
        }
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        "Ebből a fájlból nem sikerült szöveget kiolvasni. Próbáld meg bemásolni a szöveget közvetlenül.",
      );
    } finally {
      setWritingSampleBusy(false);
      if (writingSampleFileRef.current) writingSampleFileRef.current.value = "";
    }
  };

  const handleWritingSampleUrl = async (u) => {
    const trimmed = (u || "").trim();
    if (!trimmed) return;
    setWritingSampleBusy(true);
    setUrlError("");
    try {
      const res = await api.post("/writing-sample/extract-url", { url: trimmed });
      const text = res?.data?.text || "";
      if (text) {
        appendWritingSample(text);
        toast.success(`Írásminta betöltve: ${res.data.title || trimmed} (${res.data.chars} karakter)`);
        setUrl("");
        setUrlOpen(false);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Erről az oldalról nem sikerült szöveget betölteni. Másold be kézzel a legjellemzőbb részeket.";
      setUrlError(msg);
      toast.error(msg);
    } finally {
      setWritingSampleBusy(false);
    }
  };

  // INPUT-ONLY reset a gyerekkomponensben. CSAK a gépelt/kiválasztott
  // értékeket üríti — NEM módosít nézeti state-et: `mode` (Express /
  // Építő) MEGMARAD, `urlOpen` MEGMARAD (ha nyitva a URL panel, marad),
  // `demoCounter` MEGMARAD (DEMO cycling nem input). A file/URL adder
  // szám-jelzők (kbFileCount / urlAddedCount) az "input=hány forrás
  // van most" tükre — ezért 0-ra állnak, hogy konzisztens legyen az
  // ürült listával.
  const handleLocalClear = () => {
    setPasteContent("");
    setAddedUrls([]);
    setWritingSample("");
    setUrl("");
    setUrlError("");
    setKbFileCount(0);
    setUrlAddedCount(0);
    setUsedExamplePills({});
    if (typeof onContentChange === "function") {
      // A `mode`-ot NEM küldjük — a szülő `handleClear` szintén megtartja
      // a jelenlegi `contentState.mode`-ot (csak a bemeneti mezőket üríti).
      onContentChange({ paste: "", urls: [], mode, writingSample: "" });
    }
    if (typeof onClear === "function") onClear();
  };

  // Az Express Radír pill vizuálisan a Fájl / URL gombokhoz illeszkedik:
  // pontosan ugyanazok a tokenek (background/border/color/fontSize/padding/
  // borderRadius/ikon-méret). Diszkréten alacsonyabb telítettségű háttér
  // jelzi a törlő karaktert, DE ugyanabba a lila gombcsaládba tartozik.
  const expressClearPill = (
    <button
      type="button"
      onClick={handleLocalClear}
      disabled={clearing}
      data-testid="express-clear-btn"
      title="Beállítások törlése (a Riport érintetlen marad)"
      aria-label="Beállítások törlése"
      style={{
        background: "rgba(124,108,242,0.06)",
        border: "1px solid rgba(124,108,242,0.22)",
        borderRadius: 8,
        padding: "6px 12px",
        fontSize: 12,
        color: "#a89bff",
        cursor: clearing ? "wait" : "pointer",
        fontFamily: "inherit",
        display: "flex",
        alignItems: "center",
        gap: 6,
        textTransform: "uppercase",
        letterSpacing: 0,
        transition: "background 120ms",
      }}
      onMouseEnter={(e) => {
        if (clearing) return;
        e.currentTarget.style.background = "rgba(124,108,242,0.14)";
      }}
      onMouseLeave={(e) => {
        if (clearing) return;
        e.currentTarget.style.background = "rgba(124,108,242,0.06)";
      }}
    >
      {clearing ? (
        <CircleNotch size={13} weight="bold" className="mex-spin" />
      ) : (
        <Eraser size={13} weight="bold" />
      )}
      Törlés
    </button>
  );

  // Propagate UI state (mode + content sub-section + Brand Express paste +
  // Offer's avatar source id) to the parent so it can persist a draft across
  // navigation/refresh and so serializeValues() can read the avatar id.
  useEffect(() => {
    if (typeof onContentChange === "function") {
      onContentChange({
        paste: pasteContent,
        urls: addedUrls,
        mode,
        writingSample,
        avatarSourceId: selectedAvatar ? selectedAvatar.id : null,
        voiceSourceId: selectedVoice ? selectedVoice.id : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasteContent, addedUrls, mode, writingSample, selectedAvatar, selectedVoice]);

  const handleAddSplitUrl = async () => {
    const u = url.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) {
      setUrlError("Érvénytelen URL - ellenőrizd és próbáld újra.");
      return;
    }
    if (addedUrls.includes(u)) {
      toast.message("Ez az URL már hozzá van adva");
      return;
    }
    setUrlError("");
    setUrlBusy(true);
    try {
      await api.post("/rag/url", { url: u, agent_type: "foundation" });
      setAddedUrls((prev) => [...prev, u]);
      setUrl("");
      toast.success("URL feldolgozva");
    } catch (e) {
      const msg = e?.response?.data?.detail || "URL beolvasása sikertelen";
      setUrlError(msg);
    } finally {
      setUrlBusy(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full flex flex-col items-center px-6 py-12"
      style={{
        background:
          "radial-gradient(120% 70% at 50% -10%, rgba(94,80,247,0.32) 0%, rgba(94,80,247,0.12) 30%, #14102a 60%, #0c0a1c 100%)",
      }}
    >
      {/* Írásminta ingest: fájl (rejtett input, gombok programozottan
          nyitják meg). A kinyert szöveg a writingSample vagy pasteContent
          textareaba fűzve, elválasztóval. */}
      <input
        ref={writingSampleFileRef}
        type="file"
        accept=".pdf,.doc,.docx,.txt,.md"
        multiple
        onChange={(e) => handleWritingSampleFile(Array.from(e.target.files || []))}
        style={{ display: "none" }}
        data-testid="writing-sample-file-input"
      />

      {/* purple ambience layers */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 110%, rgba(94,80,247,0.22) 0%, transparent 70%), radial-gradient(40% 30% at 12% 30%, rgba(94,80,247,0.10) 0%, transparent 70%), radial-gradient(40% 30% at 88% 50%, rgba(94,80,247,0.10) 0%, transparent 70%)",
        }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-[760px]">
        {/* Hero - smaller, tighter, more elegant */}
        <div className="text-center mb-12" data-testid="agent-hero">
          <div
            className="mex-eyebrow mb-3"
            style={{ color: accent, letterSpacing: "0.18em", opacity: 0.9 }}
          >
            {eyebrow}
          </div>
          <h1
            className="font-heading tracking-tight mb-5"
            style={{
              fontSize: "clamp(30px, 3.4vw, 44px)",
              lineHeight: 1.08,
              fontWeight: 500,
              letterSpacing: "-0.018em",
            }}
          >
            {/* Render trailing ™ as a small superscript so it doesn't visually dominate */}
            {(() => {
              const m = /^(.*?)(\u2122|TM)\s*$/.exec(title || "");
              if (!m) return title;
              return (
                <>
                  {m[1].replace(/\s+$/, "")}
                  <sup style={{ fontSize: "0.42em", marginLeft: "0.05em", verticalAlign: "0.7em", fontWeight: 400, opacity: 0.7 }}>
                    {"™"}
                  </sup>
                </>
              );
            })()}
          </h1>
          <p
            className="mx-auto leading-relaxed text-[#a8a8b8]"
            style={{ fontSize: 14, maxWidth: 680 }}
          >
            {greeting || subtitle}
          </p>
        </div>

        {/* Mode selector - smaller cards */}
        <div className="mb-10">
          {modePrompt ? (
            <p
              className="text-center text-[12px] mb-5"
              style={{ color: "#7c7c8a", letterSpacing: "0.04em" }}
            >
              {modePrompt}
            </p>
          ) : null}

          <div
            className="grid grid-cols-2 gap-3.5 mx-auto"
            style={{ maxWidth: 480 }}
            data-testid="mode-selector"
          >
            {[
              { id: "fast", icon: Lightning, label: fastLabel, desc: fastDesc },
              { id: "guided", icon: ChatsTeardrop, label: guidedLabel, desc: guidedDesc },
            ].map((m) => {
              const active = mode === m.id;
              const Icon = m.icon;
              return (
                <motion.button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  data-testid={`mode-${m.id}`}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="text-center rounded-xl flex flex-col items-center justify-center"
                  style={{
                    aspectRatio: "1.15 / 1",
                    background: active
                      ? `linear-gradient(180deg, ${accent}1a 0%, ${accent}0a 100%)`
                      : "rgba(255,255,255,0.02)",
                    border: `1px solid ${active ? `${accent}aa` : "rgba(255,255,255,0.06)"}`,
                    boxShadow: active ? `0 8px 28px -10px ${accent}66` : "none",
                    cursor: "pointer",
                    padding: "20px 16px",
                    transition: "background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
                  }}
                >
                  <div
                    className="mb-3 flex items-center justify-center"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: active ? `${accent}26` : "rgba(255,255,255,0.03)",
                      border: `1px solid ${active ? `${accent}55` : "rgba(255,255,255,0.05)"}`,
                      transition: "all 0.25s ease",
                    }}
                  >
                    <Icon
                      size={22}
                      weight={active ? "fill" : "regular"}
                      color={active ? accent : "#9c9caa"}
                    />
                  </div>
                  <div
                    className="font-heading mb-1"
                    style={{ fontSize: 16, fontWeight: 500, letterSpacing: "-0.01em" }}
                  >
                    {m.label}
                  </div>
                  <div
                    className="text-[11px]"
                    style={{ color: active ? accent : "#7c7c8a", letterSpacing: "0.02em" }}
                  >
                    {m.desc}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Mode-specific content reveals only after a track is chosen */}
        <AnimatePresence mode="wait">
          {mode && (
            <motion.div
              key={mode}
              variants={containerStagger}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0, transition: { duration: 0.18 } }}
            >
              {/* F3 layer: DEMO/CLEAR fejlesztői gombok user-oldalról eltávolítva
                  (msg 442). A `handleLocalClear` mechanizmus megmarad, de csak a
                  diszkrét kis radír ikonon keresztül érhető el (lásd lentebb,
                  a steps.map() előtt). */}

              {mode === "fast" ? (
                fastVariant === "voice-source" ? (
                  <AvatarExpressVoiceSource
                    voiceSources={voiceSources}
                    selectedIdx={selectedVoiceIdx}
                    onSelect={setSelectedVoiceIdx}
                    onGenerate={handleGenerate}
                    generating={generating}
                    genProgress={genProgress}
                    accent={accent}
                    onNavigateToVoice={() => navigate("/agents/voice")}
                  />
                ) : fastVariant === "voice-source-triad" ? (
                  <motion.div variants={itemFade} className="space-y-6" data-testid="fast-body-vstriad">
                    {/* Description */}
                    <p
                      data-testid="fast-explainer"
                      style={{
                        fontSize: 14,
                        color: "#9898ad",
                        lineHeight: 1.6,
                        fontWeight: 400,
                        marginBottom: 24,
                        maxWidth: 720,
                        marginLeft: "auto",
                        marginRight: "auto",
                        textAlign: "center",
                      }}
                    >
                      Az Avatar Express egy meglévő anyagod alapján gyorsan
                      felépíti az ideális ügyfél profilját.
                    </p>

                    {/* Triád wrapper (reuses simplified Triád pattern) */}
                    <div
                      data-testid="avatar-express-triad"
                      style={{
                        background: "rgba(94,80,247,0.06)",
                        border: "1px solid rgba(94,80,247,0.28)",
                        borderRadius: 12,
                        padding: "24px 26px",
                      }}
                    >
                      {/* Header row */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 8,
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: "0.18em",
                            color: "#6a6a80",
                            textTransform: "uppercase",
                          }}
                        >
                          MÁSOLD BE AZ ÜGYFÉL-ANYAGOT
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <button
                            type="button"
                            onClick={() => writingSampleFileRef.current?.click()}
                            data-testid="avatar-triad-file-btn"
                            style={{
                              background: "rgba(124,108,242,0.10)",
                              border: "1px solid rgba(124,108,242,0.22)",
                              borderRadius: 8,
                              padding: "6px 12px",
                              fontSize: 12,
                              color: "#a89bff",
                              cursor: "pointer",
                              fontFamily: "inherit",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <UploadSimple size={13} weight="bold" />
                            Fájl
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setUrlOpen((prev) => {
                                const next = !prev;
                                if (!next) setUrlError("");
                                return next;
                              });
                            }}
                            data-testid="avatar-triad-url-btn"
                            style={{
                              background: urlOpen
                                ? "rgba(124,108,242,0.22)"
                                : "rgba(124,108,242,0.10)",
                              border: "1px solid rgba(124,108,242,0.22)",
                              borderRadius: 8,
                              padding: "6px 12px",
                              fontSize: 12,
                              color: "#a89bff",
                              cursor: "pointer",
                              fontFamily: "inherit",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <LinkIcon size={13} weight="bold" />
                            URL
                          </button>
                          {expressClearPill}
                        </div>
                      </div>

                      <textarea
                        value={writingSample}
                        onChange={(e) => setWritingSample(e.target.value)}
                        data-testid="avatar-fast-writing-sample"
                        placeholder="Másold be a saját oldalad, vagy egy konkurens szereplő sales oldalát, blogbejegyzést, vagy szöveget, amiben az ügyfeleidről, a problémájukról és vágyaikról beszélsz."
                        className="w-full text-white focus:outline-none mex-faint-ph"
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: 10,
                          padding: "12px 14px",
                          minHeight: 200,
                          resize: "vertical",
                          color: "white",
                          fontSize: 14,
                          lineHeight: 1.5,
                          fontFamily: "inherit",
                          boxSizing: "border-box",
                          width: "100%",
                        }}
                      />

                      {urlOpen && (
                        <div data-testid="avatar-triad-url-row">
                          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                            <input
                              value={url}
                              onChange={(e) => {
                                setUrl(e.target.value);
                                if (urlError) setUrlError("");
                              }}
                              onKeyDown={(e) => e.key === "Enter" && handleAddSplitUrl()}
                              data-testid="avatar-rag-url-input"
                              placeholder="https://example.com/sajat-cikk-vagy-leiras"
                              className="text-white focus:outline-none mex-faint-ph"
                              style={{
                                flex: 1,
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: 10,
                                padding: "10px 14px",
                                color: "white",
                                fontSize: 13,
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleWritingSampleUrl(url)}
                              disabled={writingSampleBusy || !url.trim()}
                              data-testid="avatar-rag-url-add"
                              style={{
                                background: "rgba(124,108,242,0.10)",
                                border: "1px solid rgba(124,108,242,0.22)",
                                borderRadius: 10,
                                padding: "10px 16px",
                                color: "#a89bff",
                                fontSize: 13,
                                cursor: writingSampleBusy ? "wait" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                opacity: !url.trim() ? 0.5 : 1,
                                fontFamily: "inherit",
                              }}
                            >
                              {writingSampleBusy ? (
                                <CircleNotch size={13} weight="bold" className="mex-spin" />
                              ) : (
                                <UploadSimple size={13} weight="bold" />
                              )}
                              Hozzáad
                            </button>
                          </div>
                          {urlError && (
                            <div style={{ fontSize: 11, color: "#d49090", marginTop: 4 }}>
                              {urlError}
                            </div>
                          )}
                        </div>
                      )}

                      <FileUpload
                        ref={fileUploadRef}
                        accentColor={accent}
                        onCountChange={setKbFileCount}
                        compact={true}
                      />

                      {addedUrls.length > 0 && (
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}
                          data-testid="avatar-fast-url-chips"
                        >
                          {addedUrls.map((u) => (
                            <span
                              key={u}
                              className="inline-flex items-center"
                              style={{
                                background: "rgba(124,108,242,0.06)",
                                border: "1px solid rgba(124,108,242,0.18)",
                                borderRadius: 8,
                                padding: "6px 10px 6px 12px",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontSize: 12,
                                color: "#d4d4e0",
                                maxWidth: 380,
                              }}
                            >
                              <LinkIcon size={12} color="#a89bff" weight="bold" />
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 220,
                                }}
                                title={u}
                              >
                                {u}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setAddedUrls((prev) => prev.filter((x) => x !== u))
                                }
                                style={{
                                  color: "#a1a1a1",
                                  background: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: 0,
                                  fontSize: 14,
                                  lineHeight: 1,
                                }}
                                aria-label="URL törlése"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      <p style={{ fontSize: 12, color: "#6a6a80", lineHeight: 1.5, marginTop: 10 }}>
                        Tipp: minél több ügyfél-megszólítás, fájdalom, vágy és élethelyzet
                        szerepel az anyagban, annál pontosabb az Avatar.
                      </p>
                    </div>
                  </motion.div>
                ) : (
                <motion.div variants={itemFade} className="space-y-6" data-testid="fast-body">
                  {/* Plain unboxed introductory text */}
                  <p
                    data-testid="fast-explainer"
                    style={{
                      fontSize: 14,
                      color: "#9898ad",
                      lineHeight: 1.6,
                      fontWeight: 400,
                      marginBottom: 24,
                      maxWidth: fastVariant === "triad" ? 640 : 960,
                      marginLeft: fastVariant === "triad" ? "auto" : undefined,
                      marginRight: fastVariant === "triad" ? "auto" : undefined,
                      textAlign: fastVariant === "triad" ? "center" : undefined,
                    }}
                  >
                    {fastVariant === "triad" ? (
                      <>
                        Az Ajánlat Express a meglévő ajánlatodat »turbózza« fel,
                        hogy még vonzóbb legyen az ügyfeleid számára. Töltsd fel
                        az ajánlatodat, és segítünk kihozni belőle a legtöbbet.
                      </>
                    ) : (
                      <>
                        Az Expressz a meglévő tartalmaid alapján kielemzi a
                        stílusodat. Tölts fel egy fájlt, illessz be egy
                        szövegmintát, vagy add meg egy weboldal linkjét a lenti
                        mezőben, és az AI másodpercek alatt visszafejti belőle
                        a márkahangodat.
                      </>
                    )}
                  </p>

                  {/* Avatar source row — centered, between intro and Triád (Offer Express) */}
                  {requiresAvatarSource && fastVariant === "triad" && (
                    <div style={{ marginBottom: 32 }}>
                      <AvatarSourceRow
                        sources={avatarSources}
                        selectedIdx={selectedAvatarIdx}
                        onSelect={setSelectedAvatarIdx}
                        onNavigateToAvatar={() => navigate("/agents/client")}
                      />
                    </div>
                  )}

                  {/* Triád wrapper - simplified: header (label + 2 buttons), textarea, optional URL row, pills, helper */}
                  <div
                    data-testid="fast-triad"
                    style={{
                      background: "rgba(94,80,247,0.06)",
                      border: "1px solid rgba(94,80,247,0.28)",
                      borderRadius: 12,
                      padding: "24px 26px",
                    }}
                  >
                    {/* Header row: label + Fájl/URL buttons */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.18em",
                          color: "#6a6a80",
                          textTransform: "uppercase",
                        }}
                      >
                        {fastVariant === "triad"
                          ? "MÁSOLD BE AZ AJÁNLATODAT"
                          : "MÁSOLD BE AZ ÍRÁSMINTÁDAT"}
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <button
                          type="button"
                          onClick={() => writingSampleFileRef.current?.click()}
                          data-testid="triad-file-btn"
                          style={{
                            background: "rgba(124,108,242,0.10)",
                            border: "1px solid rgba(124,108,242,0.22)",
                            borderRadius: 8,
                            padding: "6px 12px",
                            fontSize: 12,
                            color: "#a89bff",
                            cursor: "pointer",
                            fontFamily: "inherit",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <UploadSimple size={13} weight="bold" />
                          Fájl
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setUrlOpen((prev) => {
                              const next = !prev;
                              if (!next) setUrlError("");
                              return next;
                            });
                          }}
                          data-testid="triad-url-btn"
                          style={{
                            background: urlOpen
                              ? "rgba(124,108,242,0.22)"
                              : "rgba(124,108,242,0.10)",
                            border: "1px solid rgba(124,108,242,0.22)",
                            borderRadius: 8,
                            padding: "6px 12px",
                            fontSize: 12,
                            color: "#a89bff",
                            cursor: "pointer",
                            fontFamily: "inherit",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <LinkIcon size={13} weight="bold" />
                          URL
                        </button>
                        {expressClearPill}
                      </div>
                    </div>

                    {/* Paste textarea */}
                    <textarea
                      value={writingSample}
                      onChange={(e) => setWritingSample(e.target.value)}
                      data-testid="fast-writing-sample"
                      placeholder={
                        fastVariant === "triad"
                          ? "Illeszd be a meglévő sales lettered, ajánlat leírásod, vagy bármilyen szöveget, ami a jelenlegi ajánlatodat írja le."
                          : "Másold be egy korábbi emailedet, sales letteredet vagy blogbejegyzésedet - minél hosszabb és karakteresebb minta, annál pontosabb a márkahang."
                      }
                      className="w-full text-white focus:outline-none mex-faint-ph"
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 10,
                        padding: "12px 14px",
                        minHeight: 200,
                        resize: "vertical",
                        color: "white",
                        fontSize: 14,
                        lineHeight: 1.5,
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                        width: "100%",
                      }}
                    />

                    {/* URL input row (conditional) */}
                    {urlOpen && (
                      <div data-testid="triad-url-row">
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <input
                            value={url}
                            onChange={(e) => {
                              setUrl(e.target.value);
                              if (urlError) setUrlError("");
                            }}
                            onKeyDown={(e) => e.key === "Enter" && handleAddSplitUrl()}
                            data-testid="rag-url-input"
                            placeholder="https://example.com/sajat-cikk-vagy-leiras"
                            className="text-white focus:outline-none mex-faint-ph"
                            style={{
                              flex: 1,
                              background: "rgba(255,255,255,0.03)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              borderRadius: 10,
                              padding: "10px 14px",
                              color: "white",
                              fontSize: 13,
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleWritingSampleUrl(url)}
                            disabled={writingSampleBusy || !url.trim()}
                            data-testid="rag-url-add"
                            style={{
                              background: "rgba(124,108,242,0.10)",
                              border: "1px solid rgba(124,108,242,0.22)",
                              borderRadius: 10,
                              padding: "10px 16px",
                              color: "#a89bff",
                              fontSize: 13,
                              cursor: writingSampleBusy ? "wait" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              opacity: !url.trim() ? 0.5 : 1,
                              fontFamily: "inherit",
                            }}
                          >
                            {writingSampleBusy ? (
                              <CircleNotch size={13} weight="bold" className="mex-spin" />
                            ) : (
                              <UploadSimple size={13} weight="bold" />
                            )}
                            Hozzáad
                          </button>
                        </div>
                        {urlError && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#d49090",
                              marginTop: 4,
                            }}
                            data-testid="triad-url-error"
                          >
                            {urlError}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Hidden file input (compact) — programmatically opened via fileUploadRef */}
                    <FileUpload
                      ref={fileUploadRef}
                      accentColor={accent}
                      onCountChange={setKbFileCount}
                      compact={true}
                    />

                    {/* URL pills */}
                    {addedUrls.length > 0 && (
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}
                        data-testid="fast-url-chips"
                      >
                        {addedUrls.map((u) => (
                          <span
                            key={u}
                            className="inline-flex items-center"
                            style={{
                              background: "rgba(124,108,242,0.06)",
                              border: "1px solid rgba(124,108,242,0.18)",
                              borderRadius: 8,
                              padding: "6px 10px 6px 12px",
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              fontSize: 12,
                              color: "#d4d4e0",
                              maxWidth: 380,
                            }}
                          >
                            <LinkIcon size={12} color="#a89bff" weight="bold" />
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 220,
                              }}
                              title={u}
                            >
                              {u}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setAddedUrls((prev) => prev.filter((x) => x !== u))
                              }
                              style={{
                                color: "#a1a1a1",
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                                fontSize: 14,
                                lineHeight: 1,
                              }}
                              aria-label="URL törlése"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Helper text (last) */}
                    <p
                      style={{
                        fontSize: 12,
                        color: "#6a6a80",
                        lineHeight: 1.5,
                        marginTop: 10,
                      }}
                    >
                      {fastVariant === "triad"
                        ? "Tipp: minél több részletet kap az ajánlatról, annál erősebb a turbózott változat."
                        : "Tipp: Minél több saját szöveget adsz, annál pontosabban tudom felismerni a márkahangodat. Törekedj legalább 500 szó, vagy nagyjából egy A4-es oldalnyi saját szöveg megadására."}
                    </p>
                  </div>
                </motion.div>
                )
              ) : (
                <div data-testid="guided-body" style={{ display: "flex", flexDirection: "column", gap: eyebrowVariant === "avatar" ? 32 : 36 }}>
                  {/* Guided mode intro text — under the Ajánlat Építő card */}
                  {guidedIntro && (
                    <motion.p
                      variants={itemFade}
                      data-testid="guided-intro"
                      style={{
                        fontSize: 14,
                        color: "#9898ad",
                        lineHeight: 1.6,
                        fontWeight: 400,
                        textAlign: "center",
                        maxWidth: 640,
                        marginLeft: "auto",
                        marginRight: "auto",
                        marginBottom: 4,
                      }}
                    >
                      {guidedIntro}
                    </motion.p>
                  )}
                  {/* Avatar source row — centered, above the questions (Offer Építő) */}
                  {requiresAvatarSource && (
                    <motion.div variants={itemFade} style={{ marginBottom: 8 }}>
                      <AvatarSourceRow
                        sources={avatarSources}
                        selectedIdx={selectedAvatarIdx}
                        onSelect={setSelectedAvatarIdx}
                        onNavigateToAvatar={() => navigate("/agents/client")}
                      />
                    </motion.div>
                  )}
                  {/* Inline DEMO/CLEAR fejlesztői gombok Offer-nél (msg 442) —
                      eltávolítva. A törlés a lentebbi kis radír ikonnal érhető el. */}
                  {/* F3 építő: a radír pill az 1. kérdés sorába költözött
                      (msg 444) — lásd a q-label render alatt, i === 0 esetén. */}
                  {steps.map((s, i) => {
                    const numLabel = (i + 1).toString().padStart(2, "0");
                    const type = s.type || "textarea";
                    const useEyebrowStyle = s.eyebrowLabel !== undefined;
                    const isAvatarVariant = eyebrowVariant === "avatar";
                    return (
                      <motion.div
                        key={s.key}
                        variants={itemFade}
                        data-testid={`q-${s.key}`}
                        style={useEyebrowStyle ? { marginBottom: 8 } : {}}
                      >
                        {useEyebrowStyle ? (
                          <>
                            {/* F3 layer: a kérdés MAGA a label — uppercase, ritkított,
                                lila akcent (Landing/Webinár stílus). Prefix: sorszám
                                (01 · , 02 · , …) az egységes vizuális rendszerhez.
                                Az első lépés sorába belefér a radír pill is,
                                jobbra igazítva. */}
                            {s.question && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  gap: 16,
                                  marginBottom: 14,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    letterSpacing: "0.18em",
                                    color: accent || "#5e50f7",
                                    textTransform: "uppercase",
                                    lineHeight: 1.35,
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                  data-testid={`q-label-${s.key}`}
                                >
                                  {String(i + 1).padStart(2, "0")} · {s.question}
                                </div>
                                {i === 0 && (
                                  <button
                                    type="button"
                                    onClick={handleLocalClear}
                                    disabled={clearing}
                                    data-testid="wizard-clear-btn"
                                    title="Válaszok törlése"
                                    aria-label="Válaszok törlése"
                                    className="shrink-0 inline-flex items-center gap-1.5 transition"
                                    style={{
                                      background: `${accent || "#5e50f7"}14`,
                                      border: `1px solid ${accent || "#5e50f7"}40`,
                                      color: accent || "#5e50f7",
                                      cursor: clearing ? "wait" : "pointer",
                                      padding: "5px 11px",
                                      borderRadius: 999,
                                      fontSize: 10.5,
                                      fontWeight: 600,
                                      letterSpacing: "0.12em",
                                      textTransform: "uppercase",
                                      lineHeight: 1,
                                      marginTop: -2,
                                    }}
                                    onMouseEnter={(e) => {
                                      if (clearing) return;
                                      e.currentTarget.style.background = `${accent || "#5e50f7"}22`;
                                      e.currentTarget.style.borderColor = `${accent || "#5e50f7"}66`;
                                    }}
                                    onMouseLeave={(e) => {
                                      if (clearing) return;
                                      e.currentTarget.style.background = `${accent || "#5e50f7"}14`;
                                      e.currentTarget.style.borderColor = `${accent || "#5e50f7"}40`;
                                    }}
                                  >
                                    {clearing ? (
                                      <CircleNotch size={11} weight="bold" className="mex-spin" />
                                    ) : (
                                      <Eraser size={11} weight="bold" />
                                    )}
                                    Törlés
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {/* Legacy numbered label (Avatar / Offer) */}
                            <div
                              className="flex items-baseline"
                              style={{ marginBottom: 8 }}
                            >
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "#6a6a80",
                                  letterSpacing: "0.18em",
                                  fontWeight: 500,
                                  marginRight: 12,
                                }}
                              >
                                {numLabel}
                              </span>
                              <span
                                style={{
                                  fontSize: 14,
                                  color: "#c0c0d0",
                                  fontWeight: 500,
                                }}
                              >
                                {s.label}
                              </span>
                            </div>
                            {s.question && (
                              <div
                                style={{
                                  fontSize: 15,
                                  color: "#ffffff",
                                  fontWeight: 500,
                                  lineHeight: 1.4,
                                  marginBottom: 8,
                                }}
                              >
                                {s.question}
                              </div>
                            )}
                          </>
                        )}

                        {/* Typed field */}
                        {type === "textarea" && (
                          <textarea
                            value={values[s.key] || ""}
                            onChange={(e) => setTextValue(s.key, e.target.value)}
                            data-testid={`input-${s.key}`}
                            rows={s.rows || 4}
                            placeholder={s.placeholder || ""}
                            className="w-full text-white focus:outline-none mex-faint-ph"
                            style={{
                              background: "rgba(255,255,255,0.03)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              borderRadius: 10,
                              padding: "14px 16px",
                              color: "white",
                              fontSize: 14,
                              resize: "vertical",
                              lineHeight: 1.5,
                            }}
                          />
                        )}                        {type === "pills" && (
                          <PillsField
                            step={s}
                            value={values[s.key] || { selected: [], custom: "" }}
                            onTogglePill={(p) => togglePill(s, p)}
                            onCustomChange={(v) => setPillCustom(s, v)}
                          />
                        )}

                        {type === "sliders" && (
                          <SlidersField
                            step={s}
                            values={
                              Array.isArray(values[s.key])
                                ? values[s.key]
                                : (s.sliders || []).map(() => 50)
                            }
                            onChange={(idx, v) => setSliderValue(s, idx, v)}
                          />
                        )}

                        {type === "tags" && (
                          <TagsField
                            step={s}
                            tags={Array.isArray(values[s.key]) ? values[s.key] : []}
                            onAdd={(t) => addTag(s, t)}
                            onRemove={(t) => removeTag(s, t)}
                            usedIdx={usedExamplePills[s.key] || new Set()}
                            onUseExample={(idx) => markExamplePillUsed(s.key, idx)}
                          />
                        )}

                        {/* Helper text */}
                        {s.hint && (
                          <p
                            style={{
                              fontSize: 12,
                              color: "#6a6a80",
                              lineHeight: 1.5,
                              marginTop: 8,
                            }}
                          >
                            {s.hint}
                          </p>
                        )}
                      </motion.div>
                    );
                  })}

                  {/* Content input section (Brand Építő: simplified Triád) */}
                  {showContentInput && (contentInputMode === "split" ? (
                    <motion.div
                      variants={itemFade}
                      style={{
                        marginTop: 28,
                        marginBottom: 32,
                        background: "rgba(94,80,247,0.06)",
                        border: "1px solid rgba(94,80,247,0.28)",
                        borderRadius: 12,
                        padding: "24px 26px",
                      }}
                      data-testid="content-split"
                    >
                      {/* Header row: label + Fájl/URL buttons */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 8,
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: "0.18em",
                            color: "#6a6a80",
                            textTransform: "uppercase",
                          }}
                        >
                          MÁSOLD BE AZ ÍRÁSMINTÁDAT
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            onClick={() => writingSampleFileRef.current?.click()}
                            data-testid="content-file-btn"
                            style={{
                              background: "rgba(124,108,242,0.10)",
                              border: "1px solid rgba(124,108,242,0.22)",
                              borderRadius: 8,
                              padding: "6px 12px",
                              fontSize: 12,
                              color: "#a89bff",
                              cursor: "pointer",
                              fontFamily: "inherit",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <UploadSimple size={13} weight="bold" />
                            Fájl
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setUrlOpen((prev) => {
                                const next = !prev;
                                if (!next) setUrlError("");
                                return next;
                              });
                            }}
                            data-testid="content-url-btn"
                            style={{
                              background: urlOpen
                                ? "rgba(124,108,242,0.22)"
                                : "rgba(124,108,242,0.10)",
                              border: "1px solid rgba(124,108,242,0.22)",
                              borderRadius: 8,
                              padding: "6px 12px",
                              fontSize: 12,
                              color: "#a89bff",
                              cursor: "pointer",
                              fontFamily: "inherit",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <LinkIcon size={13} weight="bold" />
                            URL
                          </button>
                        </div>
                      </div>

                      {/* Paste textarea */}
                      <textarea
                        value={pasteContent}
                        onChange={(e) => setPasteContent(e.target.value)}
                        data-testid="content-paste"
                        placeholder="Másold be egy korábbi emailedet, sales letteredet vagy blogbejegyzésedet - minél hosszabb és karakteresebb minta, annál pontosabb a márkahang."
                        className="w-full text-white focus:outline-none mex-faint-ph"
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: 10,
                          padding: "12px 14px",
                          minHeight: 200,
                          resize: "vertical",
                          color: "white",
                          fontSize: 14,
                          lineHeight: 1.5,
                          fontFamily: "inherit",
                          boxSizing: "border-box",
                          width: "100%",
                        }}
                      />

                      {/* URL input row (conditional) */}
                      {urlOpen && (
                        <div data-testid="content-url-row">
                          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                            <input
                              value={url}
                              onChange={(e) => {
                                setUrl(e.target.value);
                                if (urlError) setUrlError("");
                              }}
                              onKeyDown={(e) => e.key === "Enter" && handleAddSplitUrl()}
                              data-testid="content-url-input"
                              placeholder="https://example.com/sajat-cikk-vagy-leiras"
                              className="text-white focus:outline-none mex-faint-ph"
                              style={{
                                flex: 1,
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: 10,
                                padding: "10px 14px",
                                color: "white",
                                fontSize: 13,
                              }}
                            />
                            <button
                              type="button"
                              onClick={handleAddSplitUrl}
                              disabled={urlBusy || !url.trim()}
                              data-testid="content-url-add"
                              style={{
                                background: "rgba(124,108,242,0.10)",
                                border: "1px solid rgba(124,108,242,0.22)",
                                borderRadius: 10,
                                padding: "10px 16px",
                                color: "#a89bff",
                                fontSize: 13,
                                cursor: urlBusy ? "wait" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                opacity: !url.trim() ? 0.5 : 1,
                                fontFamily: "inherit",
                              }}
                            >
                              {urlBusy ? (
                                <CircleNotch size={13} weight="bold" className="mex-spin" />
                              ) : (
                                <UploadSimple size={13} weight="bold" />
                              )}
                              Hozzáad
                            </button>
                          </div>
                          {urlError && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#d49090",
                                marginTop: 4,
                              }}
                              data-testid="content-url-error"
                            >
                              {urlError}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Hidden file input (compact) — programmatically opened via fileUploadRef */}
                      <FileUpload
                        ref={fileUploadRef}
                        accentColor={accent}
                        onCountChange={setKbFileCount}
                        compact={true}
                      />

                      {/* URL pills */}
                      {addedUrls.length > 0 && (
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}
                          data-testid="content-url-chips"
                        >
                          {addedUrls.map((u) => (
                            <span
                              key={u}
                              className="inline-flex items-center"
                              style={{
                                background: "rgba(124,108,242,0.06)",
                                border: "1px solid rgba(124,108,242,0.18)",
                                borderRadius: 8,
                                padding: "6px 10px 6px 12px",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontSize: 12,
                                color: "#d4d4e0",
                                maxWidth: 380,
                              }}
                            >
                              <LinkIcon size={12} color="#a89bff" weight="bold" />
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 220,
                                }}
                                title={u}
                              >
                                {u}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setAddedUrls((prev) => prev.filter((x) => x !== u))
                                }
                                style={{
                                  color: "#a1a1a1",
                                  background: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: 0,
                                  fontSize: 14,
                                  lineHeight: 1,
                                }}
                                aria-label="URL törlése"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Helper text (last) */}
                      <p
                        style={{
                          fontSize: 12,
                          color: "#6a6a80",
                          lineHeight: 1.5,
                          marginTop: 10,
                        }}
                      >
                        Tipp: Minél több saját szöveget adsz, annál pontosabban tudom felismerni a márkahangodat. A legjobb, ha bemásolsz 2-3 erős emailt, de jó lehet blogposzt, előadás szöveg, sales page vagy bármilyen saját anyag, ami jól megmutatja, hogyan kommunikálsz.
                      </p>
                    </motion.div>
                  ) : (
                  <motion.div
                    variants={itemFade}
                    className="mt-4 pt-7 border-t border-white/[0.05]"
                    data-testid="kb-section"
                  >
                    <FileUpload accentColor={accent} onCountChange={setKbFileCount} />

                    <div className="mt-5">
                      <label className="block text-[13px] text-white font-medium mb-2">
                        <LinkIcon size={13} weight="bold" className="inline mr-1.5 -mt-0.5" />
                        URL hozzáadása az Alapozás Tudásbázishoz
                      </label>
                      <div className="flex gap-2">
                        <input
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleAddUrl()}
                          data-testid="rag-url-input"
                          placeholder="https://example.com/saját-cikk-vagy-leiras"
                          className="flex-1 px-4 py-2.5 rounded-lg text-white text-[13px] focus:outline-none"
                          style={PURPLE_FIELD}
                        />
                        <button
                          onClick={handleAddUrl}
                          disabled={urlBusy || !url.trim()}
                          data-testid="rag-url-add"
                          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[12px] font-medium transition-all"
                          style={{
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            color: urlBusy ? "#6a6a78" : "#f9f8f4",
                            cursor: urlBusy ? "wait" : "pointer",
                            opacity: !url.trim() ? 0.5 : 1,
                          }}
                        >
                          {urlBusy ? (
                            <CircleNotch size={12} weight="bold" className="mex-spin" />
                          ) : (
                            <UploadSimple size={12} weight="bold" />
                          )}
                          {urlBusy ? "Beolvasás…" : "Hozzáad"}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                  ))}
                </div>
              )}

              {!(mode === "fast" && fastVariant === "voice-source") && (
              <motion.div
                variants={itemFade}
                className="flex flex-col items-center gap-2"
                style={{ marginTop: showContentInput ? 40 : 56 }}
                data-testid="generate-row"
              >
                <motion.button
                  whileHover={{ scale: canGenerate ? 1.02 : 1 }}
                  whileTap={{ scale: canGenerate ? 0.98 : 1 }}
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  data-testid="wizard-generate"
                  className="mex-btn-magic inline-flex items-center justify-center gap-2.5 rounded-xl font-semibold"
                  style={{
                    fontSize: 16,
                    padding: "16px 36px",
                    minWidth: 280,
                    letterSpacing: "-0.005em",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: canGenerate
                      ? `0 12px 40px -12px ${accent}, 0 0 0 1px ${accent}55, inset 0 1px 0 rgba(255,255,255,0.18)`
                      : "none",
                  }}
                >
                  <TopProgressBar visible={generating} />
                  {generating ? (
                    <CircleNotch size={18} weight="bold" className="mex-spin" />
                  ) : (
                    <Sparkle size={18} weight="fill" />
                  )}
                  {generating ? (genProgress || "Generálás folyamatban…") : (fastVariant === "voice-source-triad" ? "Avatar generálása" : "Blueprint generálása")}
                </motion.button>
                {genError && !generating && (
                  <div
                    data-testid="gen-error-banner"
                    style={{
                      background: "rgba(214,128,128,0.08)",
                      border: "1px solid rgba(214,128,128,0.22)",
                      borderRadius: 8,
                      padding: "10px 14px",
                      marginTop: 12,
                      fontSize: 13,
                      color: "#d49090",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 16,
                      minWidth: 280,
                    }}
                  >
                    <span>Sajnálom, valami félrement. Próbáld újra.</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (onClearGenError) onClearGenError();
                        handleGenerate();
                      }}
                      data-testid="gen-error-retry"
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#d49090",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 500,
                        padding: 0,
                        textDecoration: "underline",
                      }}
                    >
                      ↻ Újra
                    </button>
                  </div>
                )}
              </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ---------- Avatar Express variant (voice-source) ---------- */

function AvatarExpressVoiceSource({
  voiceSources,
  selectedIdx,
  onSelect,
  onGenerate,
  generating,
  genProgress,
  accent,
  onNavigateToVoice,
  hideDescription = false,
  hideGenerateButton = false,
  sourceLabel = "FORRÁS · MÁRKAHANG",
}) {
  // null = loading, [] = none, array = ready
  const loading = voiceSources === null;
  const list = Array.isArray(voiceSources) ? voiceSources : [];
  const hasAny = list.length > 0;
  const hasMultiple = list.length > 1;
  const selected = hasAny ? list[Math.min(selectedIdx, list.length - 1)] : null;
  const [open, setOpen] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest("[data-testid='voice-source-bubble']")) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}.${m}.${day}`;
    } catch {
      return "";
    }
  };

  const handlePick = (idx) => {
    onSelect(idx);
    setOpen(false);
  };

  return (
    <motion.div variants={itemFade} data-testid="avatar-express-voice" style={{ marginTop: hideDescription ? 0 : 40 }}>
      {/* Description */}
      {!hideDescription && (
      <p
        data-testid="avatar-express-desc"
        style={{
          fontSize: 14,
          color: "#9898ad",
          lineHeight: 1.6,
          maxWidth: 720,
          marginLeft: "auto",
          marginRight: "auto",
          textAlign: "center",
        }}
      >
        Az Avatar Express a korábban elmentett Márkahangod alapján építi fel az
        ideális ügyfél-avatart. Pár másodperc alatt elkészül.
      </p>
      )}

      {/* Source row */}
      <div
        style={{
          marginTop: hideDescription ? 0 : 32,
          maxWidth: 480,
          marginLeft: "auto",
          marginRight: "auto",
          textAlign: "center",
        }}
        data-testid="avatar-express-source"
      >
        {loading ? (
          <div style={{ fontSize: 13, color: "#6a6a80" }}>Forrás betöltése…</div>
        ) : !hasAny ? (
          /* No saved Voice — rich empty state with nav to Voice agent. */
          <>
            <div style={{ fontSize: 15, color: "#d4d4e0", fontWeight: 500, marginBottom: 6 }}>
              Még nincs mentett Márkahangod.
            </div>
            <div style={{ fontSize: 13, color: "#9898ad", marginBottom: 16 }}>
              Először a Voice ügynökben készítsd el.
            </div>
            <button
              type="button"
              onClick={onNavigateToVoice}
              data-testid="avatar-express-goto-voice"
              className="inline-flex items-center"
              style={{
                background: "rgba(124,108,242,0.10)",
                border: "1px solid rgba(124,108,242,0.25)",
                color: "#a89bff",
                padding: "8px 16px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                gap: 6,
              }}
            >
              <ArrowRight size={13} weight="bold" /> Voice ügynök
            </button>
          </>
        ) : !hasMultiple ? (
          /* Smart defaulting: only one source → hide row, auto-use it. */
          null
        ) : (
          <>
            <div
              style={{
                fontSize: 14,
                fontWeight: 400,
                color: "#6a6a80",
                letterSpacing: "0.20em",
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              {sourceLabel}
            </div>
            {/* Purple bubble — clickable when multiple voices exist */}
            <div
              style={{ position: "relative", display: "inline-block" }}
              data-testid="voice-source-bubble"
            >
              <button
                type="button"
                onClick={() => hasMultiple && setOpen((v) => !v)}
                disabled={!hasMultiple}
                data-testid="avatar-express-source-value"
                className="inline-flex items-center"
                style={{
                  background: "rgba(124,108,242,0.10)",
                  border: "1px solid rgba(124,108,242,0.30)",
                  borderRadius: 999,
                  padding: hasMultiple ? "8px 14px 8px 16px" : "8px 16px",
                  fontSize: 14,
                  color: "#d4d4e0",
                  fontWeight: 500,
                  cursor: hasMultiple ? "pointer" : "default",
                  gap: 8,
                  letterSpacing: "-0.005em",
                  transition: "background 0.15s ease, border-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (hasMultiple) {
                    e.currentTarget.style.background = "rgba(124,108,242,0.16)";
                    e.currentTarget.style.borderColor = "rgba(124,108,242,0.45)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (hasMultiple) {
                    e.currentTarget.style.background = "rgba(124,108,242,0.10)";
                    e.currentTarget.style.borderColor = "rgba(124,108,242,0.30)";
                  }
                }}
              >
                <span>
                  {selected.name} · mentve {formatDate(selected.saved_at)}
                </span>
                {hasMultiple && (
                  <CaretDown
                    size={11}
                    weight="bold"
                    style={{
                      color: "#a89bff",
                      transition: "transform 0.18s ease",
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                )}
              </button>

              {/* Dropdown — shown only when multiple voices and open */}
              {hasMultiple && open && (
                <div
                  data-testid="voice-source-dropdown"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#16161e",
                    border: "1px solid rgba(124,108,242,0.25)",
                    borderRadius: 12,
                    boxShadow: "0 16px 48px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(124,108,242,0.10)",
                    padding: 6,
                    minWidth: 320,
                    maxWidth: 420,
                    zIndex: 50,
                    textAlign: "left",
                  }}
                >
                  {list.map((v, i) => {
                    const active = i === selectedIdx;
                    return (
                      <button
                        type="button"
                        key={v.id}
                        onClick={() => handlePick(i)}
                        data-testid={`voice-source-option-${i}`}
                        className="w-full inline-flex items-center"
                        style={{
                          background: active ? "rgba(124,108,242,0.12)" : "transparent",
                          border: "none",
                          padding: "10px 12px",
                          borderRadius: 8,
                          fontSize: 13,
                          color: active ? "#e8e8f0" : "#c0c0d0",
                          cursor: "pointer",
                          gap: 10,
                          textAlign: "left",
                          transition: "background 0.12s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                        }}
                        onMouseLeave={(e) => {
                          if (!active) e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span
                          style={{
                            width: 14,
                            display: "inline-flex",
                            justifyContent: "center",
                            color: "#a89bff",
                            flexShrink: 0,
                          }}
                        >
                          {active ? <Check size={12} weight="bold" /> : null}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {v.name}
                          </div>
                          <div style={{ fontSize: 11, color: "#6a6a80", marginTop: 1 }}>
                            mentve {formatDate(v.saved_at)}
                          </div>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Avatar generálása button */}
      {hasAny && !hideGenerateButton && (
        <div style={{ marginTop: 32, textAlign: "center" }}>
          <motion.button
            whileHover={{ scale: !generating ? 1.02 : 1 }}
            whileTap={{ scale: !generating ? 0.98 : 1 }}
            onClick={onGenerate}
            disabled={generating}
            data-testid="avatar-express-generate"
            className="inline-flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #7c6cf2, #5e4cd8)",
              color: "white",
              padding: "14px 24px",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              position: "relative",
              overflow: "hidden",
              boxShadow: !generating
                ? `0 8px 28px rgba(124,108,242,0.35), 0 0 0 1px ${accent}55, inset 0 1px 0 rgba(255,255,255,0.18)`
                : "none",
              border: "none",
              cursor: generating ? "wait" : "pointer",
              minWidth: 280,
              gap: 10,
              opacity: generating ? 0.85 : 1,
            }}
          >
            <TopProgressBar visible={generating} />
            {generating ? (
              <CircleNotch size={16} weight="bold" className="mex-spin" />
            ) : (
              <Sparkle size={16} weight="fill" />
            )}
            {generating ? (genProgress || "Generálás folyamatban…") : "Avatar generálása"}
          </motion.button>
        </div>
      )}
    </motion.div>
  );
}

/* ---------- Ajánlat Varázsló: centered Avatar source bubble ----------
 * Same visual design as AvatarExpressVoiceSource — centered FORRÁS · AVATAR
 * eyebrow + large purple bubble with dropdown. Used in both Offer Express
 * (below intro text, above Triád) and Offer Építő (below intro text, above
 * Q01-Q04).
 */

function AvatarSourceRow({ sources, selectedIdx, onSelect, onNavigateToAvatar }) {
  const loading = sources === null;
  const list = Array.isArray(sources) ? sources : [];
  const hasAny = list.length > 0;
  const hasMultiple = list.length > 1;
  const selected = hasAny ? list[Math.min(selectedIdx, list.length - 1)] : null;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest("[data-testid='avatar-source-bubble']")) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
    } catch {
      return "";
    }
  };

  // "Réka · mentve 2026.05.02" — include name only; dropdown rows show meta.
  const labelFor = (item) => {
    const bp = item?.blueprint || {};
    const ak = bp.alapkep || {};
    const nev = (ak.nev || "").trim();
    return nev || item?.name || "Mentett Avatar";
  };

  const metaFor = (item) => {
    const bp = item?.blueprint || {};
    const ak = bp.alapkep || {};
    const kor = (ak.kor || "").toString().trim();
    const szakma = (ak.szakma || "").trim();
    const parts = [];
    if (kor) parts.push(/^\d/.test(kor) ? `${kor} éves` : kor);
    if (szakma) parts.push(szakma);
    return parts.join(", ");
  };

  const handlePick = (idx) => {
    onSelect(idx);
    setOpen(false);
  };

  // Smart defaulting: hide the row entirely when only one source exists.
  // Generation auto-uses that one. The "no source" empty state still
  // renders so the user can navigate to the Avatar agent.
  if (!loading && hasAny && !hasMultiple) {
    return null;
  }

  return (
    <div
      data-testid="avatar-source-row"
      style={{
        textAlign: "center",
        maxWidth: 480,
        marginLeft: "auto",
        marginRight: "auto",
      }}
    >
      {loading ? (
        <div style={{ fontSize: 13, color: "#6a6a80" }}>Forrás betöltése…</div>
      ) : (
        <>
          <div
            style={{
              fontSize: 14,
              fontWeight: 400,
              color: "#6a6a80",
              letterSpacing: "0.20em",
              textTransform: "uppercase",
              marginBottom: 10,
            }}
            data-testid="avatar-source-eyebrow"
          >
            AVATAR
          </div>

          {hasAny ? (
            <div
              style={{ position: "relative", display: "inline-block" }}
              data-testid="avatar-source-bubble"
            >
              <button
                type="button"
                onClick={() => hasMultiple && setOpen((v) => !v)}
                disabled={!hasMultiple}
                data-testid="avatar-source-value"
                className="inline-flex items-center"
                style={{
                  background: "rgba(124,108,242,0.10)",
                  border: "1px solid rgba(124,108,242,0.30)",
                  borderRadius: 999,
                  padding: hasMultiple ? "8px 14px 8px 16px" : "8px 16px",
                  fontSize: 14,
                  color: "#d4d4e0",
                  fontWeight: 500,
                  cursor: hasMultiple ? "pointer" : "default",
                  gap: 8,
                  letterSpacing: "-0.005em",
                  transition: "background 0.15s ease, border-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (hasMultiple) {
                    e.currentTarget.style.background = "rgba(124,108,242,0.16)";
                    e.currentTarget.style.borderColor = "rgba(124,108,242,0.45)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (hasMultiple) {
                    e.currentTarget.style.background = "rgba(124,108,242,0.10)";
                    e.currentTarget.style.borderColor = "rgba(124,108,242,0.30)";
                  }
                }}
              >
                <span>
                  {labelFor(selected)}
                  {metaFor(selected) ? ` · ${metaFor(selected)}` : ""}
                  {` · mentve ${formatDate(selected.saved_at)}`}
                </span>
                {hasMultiple && (
                  <CaretDown
                    size={11}
                    weight="bold"
                    style={{
                      color: "#a89bff",
                      transition: "transform 0.18s ease",
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                )}
              </button>

              {hasMultiple && open && (
                <div
                  data-testid="avatar-source-dropdown"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#16161e",
                    border: "1px solid rgba(124,108,242,0.25)",
                    borderRadius: 12,
                    boxShadow:
                      "0 16px 48px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(124,108,242,0.10)",
                    padding: 6,
                    minWidth: 320,
                    maxWidth: 420,
                    zIndex: 50,
                    textAlign: "left",
                  }}
                >
                  {list.map((v, i) => {
                    const active = i === selectedIdx;
                    const meta = metaFor(v);
                    return (
                      <button
                        type="button"
                        key={v.id}
                        onClick={() => handlePick(i)}
                        data-testid={`avatar-source-option-${i}`}
                        className="w-full inline-flex items-center"
                        style={{
                          background: active ? "rgba(124,108,242,0.12)" : "transparent",
                          border: "none",
                          padding: "10px 12px",
                          borderRadius: 8,
                          fontSize: 13,
                          color: active ? "#e8e8f0" : "#c0c0d0",
                          cursor: "pointer",
                          gap: 10,
                          textAlign: "left",
                          transition: "background 0.12s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                        }}
                        onMouseLeave={(e) => {
                          if (!active) e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span
                          style={{
                            width: 14,
                            display: "inline-flex",
                            justifyContent: "center",
                            color: "#a89bff",
                            flexShrink: 0,
                          }}
                        >
                          {active ? <Check size={12} weight="bold" /> : null}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {labelFor(v)}
                            {meta ? ` · ${meta}` : ""}
                          </div>
                          <div style={{ fontSize: 11, color: "#6a6a80", marginTop: 1 }}>
                            mentve {formatDate(v.saved_at)}
                          </div>
                        </span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={onNavigateToAvatar}
                    data-testid="avatar-source-goto"
                    className="w-full inline-flex items-center"
                    style={{
                      marginTop: 4,
                      background: "transparent",
                      border: "1px dashed rgba(124,108,242,0.30)",
                      padding: "9px 12px",
                      borderRadius: 8,
                      fontSize: 12,
                      color: "#a89bff",
                      cursor: "pointer",
                      gap: 8,
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(124,108,242,0.08)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <ArrowRight size={11} weight="bold" /> → Avatar Varázsló
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Empty state — inline notice + link */
            <div
              data-testid="avatar-source-empty"
              style={{
                background: "rgba(124,108,242,0.06)",
                border: "1px solid rgba(124,108,242,0.22)",
                borderRadius: 999,
                padding: "10px 18px",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                fontSize: 13,
                color: "#9898ad",
              }}
            >
              <span>Még nincs mentett Avatar</span>
              <button
                type="button"
                onClick={onNavigateToAvatar}
                data-testid="goto-avatar"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#a89bff",
                  padding: 0,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                }}
              >
                → Avatar Varázsló
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---------- Typed-step sub-components (Brand Építő only) ---------- */

/** Thin animated progress bar at the top edge of a button.
 *  Absolute-positioned, 2px tall, indeterminate gradient sweep.
 *  Parent button must have `position: relative` and `overflow: hidden`. */
function TopProgressBar({ visible }) {
  if (!visible) return null;
  return (
    <div
      data-testid="btn-top-progress"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        background: "rgba(255,255,255,0.12)",
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          width: "35%",
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.95) 50%, transparent 100%)",
        }}
        animate={{ left: ["-35%", "100%"] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
}

function PillsField({ step, value, onTogglePill, onCustomChange }) {
  const selected = Array.isArray(value?.selected) ? value.selected : [];
  const custom = value?.custom || "";
  const customCount = custom.trim() ? 1 : 0;
  const total = selected.length + customCount;
  const atCap = total >= (step.maxSelect ?? 4);
  const options = step.options || [];
  return (
    <div>
      <div className="flex flex-wrap" style={{ gap: 8 }} data-testid={`pills-${step.key}`}>
        {options.map((opt) => {
          const isSel = selected.includes(opt);
          const disabled = !isSel && atCap;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onTogglePill(opt)}
              disabled={disabled}
              data-testid={`pill-${step.key}-${opt}`}
              style={{
                background: isSel ? "rgba(124,108,242,0.15)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${isSel ? "rgba(124,108,242,0.35)" : "rgba(255,255,255,0.08)"}`,
                color: isSel ? "#d4ccff" : "#c0c0d0",
                padding: "9px 14px",
                borderRadius: 10,
                fontSize: 13,
                fontWeight: isSel ? 500 : 400,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.4 : 1,
                transition: "background 0.15s, border-color 0.15s",
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
      <input
        value={custom}
        onChange={(e) => onCustomChange(e.target.value)}
        placeholder={step.customPlaceholder || "Más érzés…"}
        data-testid={`pill-custom-${step.key}`}
        className="w-full mt-3 text-white focus:outline-none mex-faint-ph"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 10,
          padding: "10px 14px",
          color: "white",
          fontSize: 14,
        }}
      />
      {step.customHint && (
        <p
          style={{
            fontSize: 12,
            color: "#6a6a80",
            lineHeight: 1.5,
            marginTop: 8,
          }}
          data-testid={`pill-custom-hint-${step.key}`}
        >
          {step.customHint}
        </p>
      )}
      {atCap && (
        <p style={{ fontSize: 11, color: "#8a7df0", marginTop: 6 }}>
          Elérted a {step.maxSelect ?? 4} maximumot. Egyet kapcsolj ki, ha cserélnél.
        </p>
      )}
    </div>
  );
}

function SlidersField({ step, values, onChange }) {
  const sliders = step.sliders || [];
  return (
    <div data-testid={`sliders-${step.key}`}>
      {sliders.map((pair, idx) => (
        <div key={idx} style={{ marginBottom: 18 }}>
          <div
            className="flex justify-between"
            style={{ fontSize: 12, color: "#9898ad", marginBottom: 6 }}
          >
            <span style={{ textAlign: "left" }}>{pair.left}</span>
            <span style={{ textAlign: "right" }}>{pair.right}</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={values[idx] ?? 50}
            onChange={(e) => onChange(idx, parseInt(e.target.value, 10))}
            data-testid={`slider-${step.key}-${idx}`}
            className="mex-slider w-full"
            style={{
              "--mex-slider-pct": `${values[idx] ?? 50}%`,
            }}
          />
        </div>
      ))}
    </div>
  );
}

function TagsField({ step, tags, onAdd, onRemove, usedIdx, onUseExample }) {
  const [draft, setDraft] = useState("");
  const commit = () => {
    const text = draft;
    if (!text.trim()) return;
    const parts = text.split(",").map((s) => s.trim()).filter(Boolean);
    parts.forEach((p) => onAdd(p));
    setDraft("");
  };
  const examples = step.examplePills || [];
  return (
    <div>
      <div
        data-testid={`tag-input-${step.key}`}
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 10,
          padding: "10px 12px",
          minHeight: 44,
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          alignItems: "center",
        }}
      >
        {tags.map((t) => (
          <span
            key={t}
            data-testid={`tag-${t}`}
            className="inline-flex items-center"
            style={{
              background: "rgba(214,128,128,0.10)",
              border: "1px solid rgba(214,128,128,0.25)",
              color: "#f0c0c0",
              padding: "4px 10px",
              borderRadius: 12,
              fontSize: 12,
              gap: 6,
            }}
          >
            {t}
            <button
              type="button"
              onClick={() => onRemove(t)}
              style={{
                color: "#f0c0c0",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 0,
                fontSize: 12,
                lineHeight: 1,
              }}
              aria-label={`Törlés: ${t}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            } else if (e.key === "Backspace" && !draft && tags.length > 0) {
              onRemove(tags[tags.length - 1]);
            }
          }}
          onBlur={commit}
          placeholder={tags.length === 0 ? (step.placeholder || "") : ""}
          className="flex-1 outline-none"
          style={{
            background: "transparent",
            border: "none",
            color: "white",
            fontSize: 13,
            minWidth: 140,
            padding: "2px 0",
          }}
        />
      </div>
      {examples.length > 0 && (
        <div
          className="flex flex-wrap"
          style={{ gap: 6, marginTop: 12 }}
          data-testid={`tag-examples-${step.key}`}
        >
          {examples.map((ex, idx) => {
            const used = usedIdx.has(idx);
            return (
              <button
                key={idx}
                type="button"
                disabled={used}
                onClick={() => {
                  if (used) return;
                  const added = onAdd(ex);
                  if (added) onUseExample(idx);
                }}
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px dashed rgba(214,128,128,0.30)",
                  color: "#b8b8c8",
                  padding: "5px 11px",
                  borderRadius: 12,
                  fontSize: 11,
                  cursor: used ? "default" : "pointer",
                  opacity: used ? 0.4 : 1,
                  pointerEvents: used ? "none" : "auto",
                }}
              >
                {ex}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

